import type { MouseEvent } from 'react';

/**
 * Two routes on the History API: home is the owner's own profile, and `/p/<slug>` is a profile
 * link (R11). Sign-in builds its return URL from these (`auth-config.ts`), so a visitor asked to
 * sign in on a link comes back to that link.
 */
export type Route = { name: 'home' } | { name: 'profile'; slug: string };

const decode = (value: string) => { try { return decodeURIComponent(value); } catch { return null; } };

export function parseRoute(pathname: string): Route {
  const match = /^\/p\/([^/]+)\/?$/.exec(pathname);
  const slug = match ? decode(match[1]!) : null;
  return slug ? { name: 'profile', slug } : { name: 'home' };
}

export const HOME_HREF = '/';
export const profileHref = (slug: string) => `/p/${encodeURIComponent(slug)}`;

/** The canonical path for a route; anything unrecognised is home. */
export function routeHref(route: Route): string {
  return route.name === 'profile' ? profileHref(route.slug) : HOME_HREF;
}

/** Moves to a route in place; App listens for `popstate`, so Back and Forward work the same way. */
export function navigate(href: string) {
  window.history.pushState(null, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** An in-app link that still opens in a new tab on a modified click. */
export const linkTo = (href: string, before?: () => void) => ({
  href,
  onClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!isPlainClick(event)) return;
    event.preventDefault(); before?.(); navigate(href);
  },
});

function isPlainClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;
}
